import { CartTotals } from '@/components/cart/CartPassLine';
import { Loader2 } from 'lucide-react';
import { trackPassesInCart } from '@/utils/eventPassTracking';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { getCart, addToCart, removeFromCart, PRICES } from '@/utils/cart';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getPostHog, trackCheckoutInitiated, trackUpsellAccepted, trackUpsellPresented, trackUpsellRejected } from '@/hooks/usePostHogTracking';
import { captureEdgeFunctionError, trackSupabaseFunctionError } from '@/utils/errorTracking';
import { getUtmParams } from '@/utils/utmCapture';
import { UpsellModal } from './UpsellModal';
import { CartShareModal } from './CartShareModal';
import { Share2 } from 'lucide-react';

interface CartCheckoutProps {
  className?: string;
}

export const CartCheckout: React.FC<CartCheckoutProps> = ({ className = "" }) => {
  const { t } = useTranslation();
  const ph = getPostHog();
  const variant = ph?.getFeatureFlag?.('show-collection-upsell-threshold') ?? null;
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const cart = getCart();

  // Determine threshold based on feature flag variant
  const photoThreshold = variant === '2-photos' ? 2 : 3;

  // Detect upsell opportunities (3+ single photos of same participant)
  // Only show if we don't lose revenue (delta >= 0, meaning customer pays same or more)
  // Exclude winner ceremony and group race photos from upsell
  const upsellOpportunities = cart.items.reduce((acc, item) => {
    if (item.type === 'single' && !item.isGroup && !item.isWinner) {
      const key = `${item.participantId}-${item.raceId}`;
      if (!acc[key]) {
        acc[key] = {
          participantId: item.participantId,
          participantName: item.participantName,
          raceId: item.raceId,
          raceName: item.raceCategory,
          eventId: item.eventId,
          photoIds: [],
          currentPhotos: 0,
          currentCost: 0,
        };
      }
      acc[key].photoIds.push(item.photoId!);
      acc[key].currentPhotos += item.quantity;
      acc[key].currentCost += item.price * item.quantity;
    }
    return acc;
  }, {} as Record<string, any>);

  const opportunities = Object.values(upsellOpportunities)
    .filter((opp: any) => opp.currentPhotos >= photoThreshold)
    .map((opp: any) => ({
      ...opp,
      collectionCost: PRICES.COLLECTION,
      delta: PRICES.COLLECTION - opp.currentCost,
    }))
    .filter((opp: any) => opp.delta >= 0); // Only show if we don't lose revenue

  const handleUpgradeAndProceed = (participantIds: string[]) => {
    // Get the accepted opportunities for tracking
    const acceptedOpportunities = opportunities.filter(opp => 
      participantIds.includes(opp.participantId)
    );

    // Track upsell acceptance
    trackUpsellAccepted(ph, acceptedOpportunities);

    // Remove individual photos and add collections
    participantIds.forEach(participantId => {
      const opp = opportunities.find(o => o.participantId === participantId);
      if (opp) {
        opp.photoIds.forEach((photoId: number) => {
          removeFromCart(participantId, 'single', photoId);
        });
        const item = cart.items.find(i => i.participantId === participantId);
        if (item) {
          addToCart({
            participantId: item.participantId,
            swissEquestrianRiderId: item.swissEquestrianRiderId,
            participantName: item.participantName,
            horseName: item.horseName,
            raceCategory: item.raceCategory,
            eventName: item.eventName,
            raceId: item.raceId,
            eventId: item.eventId,
            type: 'collection',
            price: PRICES.COLLECTION,
          });
        }
      }
    });

    // Proceed to checkout WITHOUT rejection tracking
    proceedToCheckout(false);
  };

  const initiateCheckout = () => {
    console.log('Initiating checkout, opportunities:', opportunities.length);
    if (opportunities.length > 0) {
      console.log('Showing upsell modal');
      // Track upsell presentation
      trackUpsellPresented(ph, opportunities);
      setShowUpsellModal(true);
    } else {
      console.log('No opportunities, proceeding directly');
      proceedToCheckout();
    }
  };

  const proceedToCheckout = async (fromUpsellModal = false) => {
    // Track rejection if coming from upsell modal
    if (fromUpsellModal && opportunities.length > 0) {
      trackUpsellRejected(ph, opportunities);
    }
    
    setShowUpsellModal(false);
    setIsCreatingCheckout(true);
    
    // Get fresh cart after potential upgrades
    const currentCart = getCart();
    
    // Track checkout initiation
    trackCheckoutInitiated(ph, currentCart);
    trackPassesInCart('checkout_started', currentCart.items, { source: 'checkout' });
    
    try {
      const utmParams = getUtmParams();
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { cartItems: currentCart.items, totalAmount: currentCart.total, ...utmParams }
      });
      
      if (error) {
        console.error('Supabase function error:', error);
        captureEdgeFunctionError('create-checkout-session', error, {
          cart_items: currentCart.items,
          total_amount: currentCart.total
        });
        throw new Error(error.message || 'Failed to create checkout session');
      }
      
      if (data?.error) {
        console.error('Checkout session error:', data.error);
        trackSupabaseFunctionError('create-checkout-session', data, {
          cart_items: currentCart.items,
          total_amount: currentCart.total
        });
        throw new Error(data.error);
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        trackSupabaseFunctionError('create-checkout-session', { status: 200, error: 'No URL' }, {
          cart_items: currentCart.items,
          total_amount: currentCart.total
        });
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      // Capture the error with PostHog if not already captured
      if (!error.message?.includes('Failed to create checkout session')) {
        captureEdgeFunctionError('create-checkout-session', error, {
          cart_items: currentCart.items,
          total_amount: currentCart.total
        });
      }
      
      let errorMessage = 'Checkout failed';
      if (error.message.includes('Stripe products not configured')) {
        errorMessage = 'Payment system not configured. Please contact support.';
      } else if (error.message.includes('Stripe settings not found')) {
        errorMessage = 'Payment system not set up. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  if (cart.items.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`bg-background p-4 shadow-[0_-1px_12px_hsl(var(--foreground)/0.06)] ${className}`}>
        <div className="max-w-md mx-auto space-y-3">
          <CartTotals items={cart.items} total={cart.total} />

          <Button 
            className="w-full transition-transform duration-150 enabled:active:scale-[0.96]" 
            size="lg"
            onClick={initiateCheckout}
            disabled={isCreatingCheckout}
          >
            {isCreatingCheckout && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreatingCheckout ? t('cart.processing') : t('cart.checkout')}
          </Button>
          <p className="text-center text-xs text-muted-foreground [text-wrap:pretty]">
            {t('eventPass.reassurance')} · {t('cart.paymentMethods')}
          </p>

          <Button
            variant="ghost"
            className="w-full text-primary"
            size="lg"
            onClick={() => setShowShareModal(true)}
            disabled={isCreatingCheckout}
          >
            <Share2 className="mr-2 h-4 w-4" />
            {t('cartShare.shareWithGuardian', 'Condividi con un genitore')}
          </Button>
        </div>
      </div>

      <UpsellModal
        isOpen={showUpsellModal}
        onClose={() => setShowUpsellModal(false)}
        opportunities={opportunities}
        onUpgradeAndProceed={handleUpgradeAndProceed}
        onDecline={() => proceedToCheckout(true)}
      />

      <CartShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};
